import React, { useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

import vokuMark from "@/assets/voku-mark.jpeg";
import targetDesign from "@/assets/ops/target-design.jpeg";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  ArrowUpRight,
  Crosshair,
  Shield,
  Zap,
  HardDrive,
  Radio,
  ChevronRight,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function RequestPocDialog({ children }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    org: "",
    message: "",
  });

  const canSubmit =
    form.name.trim().length >= 2 &&
    form.email.trim().includes("@") &&
    form.message.trim().length >= 10;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || loading) return;

    setLoading(true);
    try {
      await axios.post(`${API}/access-requests`, {
        name: form.name,
        email: form.email,
        use_case: "tactical",
        message: `${form.org ? `Org: ${form.org}\n\n` : ""}${form.message}`,
        source: "ops_page",
      });

      toast.success("Request received", {
        description: "We’ll follow up with a PoC briefing if your request is approved.",
      });
      setOpen(false);
      setForm({ name: "", email: "", org: "", message: "" });
    } catch (err) {
      toast.error("Couldn’t submit", {
        description: "Please try again in a moment.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-[620px] rounded-none border-white/15 bg-black/75 text-white backdrop-blur-2xl">
        <DialogHeader>
          <DialogTitle className="text-white">Request PoC Briefing</DialogTitle>
          <DialogDescription className="text-white/70">
            Provide a minimal operational context and test environment.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="mt-2 space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-xs font-mono uppercase tracking-[0.18em] text-white/60">
                Name
              </label>
              <Input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="Your name"
                className="h-11 rounded-none border-white/15 bg-black/40 font-mono text-white placeholder:text-white/40 focus-visible:ring-[#00E6A8]/70"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-mono uppercase tracking-[0.18em] text-white/60">
                Email
              </label>
              <Input
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="you@org.mil"
                type="email"
                className="h-11 rounded-none border-white/15 bg-black/40 font-mono text-white placeholder:text-white/40 focus-visible:ring-[#00E6A8]/70"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-mono uppercase tracking-[0.18em] text-white/60">
              Org / Unit (optional)
            </label>
            <Input
              value={form.org}
              onChange={(e) => setForm((p) => ({ ...p, org: e.target.value }))}
              placeholder="Org / Unit"
              className="h-11 rounded-none border-white/15 bg-black/40 font-mono text-white placeholder:text-white/40 focus-visible:ring-[#00E6A8]/70"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-mono uppercase tracking-[0.18em] text-white/60">
              Mission fit
            </label>
            <Textarea
              value={form.message}
              onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
              placeholder="Use-case (CQB, FPV, thermal/NVG), constraints, integration points, and desired timeline."
              className="min-h-[120px] rounded-none border-white/15 bg-black/40 font-mono text-white placeholder:text-white/40 focus-visible:ring-[#00E6A8]/70"
            />
            <div className="text-xs text-white/45">Minimum 10 characters.</div>
          </div>

          <div className="flex flex-col gap-3 pt-2 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2 text-xs text-white/55">
              <Shield className="h-4 w-4 text-white/60" />
              <span>Offline-first intake. No trackers.</span>
            </div>
            <Button
              type="submit"
              disabled={!canSubmit || loading}
              className="h-11 rounded-none bg-[#00E6A8] text-black hover:bg-white hover:text-black disabled:opacity-60"
            >
              {loading ? "Submitting…" : "Submit"}
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function OpsHeader() {
  const links = useMemo(
    () => [
      { label: "Problem", id: "problem" },
      { label: "Solution", id: "solution" },
      { label: "Use Cases", id: "use-cases" },
      { label: "Status", id: "status" },
    ],
    [],
  );

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header className="fixed left-0 right-0 top-0 z-50 border-b border-white/10 bg-black/85 backdrop-blur-xl">
      <div className="mx-auto flex h-[76px] max-w-6xl items-center justify-between px-5 md:px-8">
        <Link to="/" className="group flex items-center gap-3">
          <div className="relative grid h-9 w-9 place-items-center overflow-hidden rounded-none border border-white/15 bg-white/[0.02]">
            <div
              className="absolute -inset-1 -z-10 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-100"
              style={{
                background:
                  "radial-gradient(24px 24px at 50% 50%, rgba(0,230,168,0.22), transparent 70%)",
              }}
            />
            <img
              src={vokuMark}
              alt="VOKU mark"
              className="h-full w-full object-cover opacity-95"
            />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold tracking-[0.14em] text-white">
              VOKU OPS
            </div>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-white/45">
              Tactical Analog HUD
            </div>
          </div>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => scrollTo(l.id)}
              className="text-sm text-white/55 transition-colors hover:text-white"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <RequestPocDialog>
          <Button className="h-10 rounded-none bg-[#00E6A8] text-black hover:bg-white hover:text-black">
            Request PoC
            <ArrowUpRight className="ml-2 h-4 w-4" />
          </Button>
        </RequestPocDialog>
      </div>
    </header>
  );
}

function TacticalBackdrop() {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10">
      {/* grid */}
      <div className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(to_right,rgba(255,255,255,0.10)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.10)_1px,transparent_1px)] [background-size:78px_78px]" />
      {/* crosshair / telemetry */}
      <div className="absolute inset-0">
        <div className="absolute left-[8%] top-[18%] h-[260px] w-[260px] rounded-full border border-white/10" />
        <div className="absolute left-[8%] top-[18%] h-[260px] w-[260px]">
          <div className="absolute left-1/2 top-0 h-full w-px bg-white/10" />
          <div className="absolute top-1/2 h-px w-full bg-white/10" />
        </div>
        <div className="absolute right-[6%] top-[10%] h-[2px] w-[420px] bg-white/10" />
        <div className="absolute right-[6%] top-[10%] h-[2px] w-[140px] bg-[#00E6A8]/40" />
      </div>
      {/* subtle green glow */}
      <div className="absolute inset-0 bg-[radial-gradient(820px_520px_at_65%_12%,rgba(0,230,168,0.10),transparent_60%),radial-gradient(720px_520px_at_12%_70%,rgba(255,255,255,0.04),transparent_60%)]" />
    </div>
  );
}

export default function Ops() {
  const rows = useMemo(
    () => [
      {
        feature: "Latency",
        digital: "15–50ms+ (Induces nausea)",
        voku: "<1ms (Instantaneous)",
      },
      {
        feature: "OS/Compute",
        digital: "Windows/Android (Crash risk)",
        voku: "None (Hardware-only)",
      },
      {
        feature: "EW Vulnerability",
        digital: "High (RF emissions)",
        voku: "Zero (Hardwired, unjammable)",
      },
      {
        feature: "Light Transmittance",
        digital: "Tinted visors (Blind spots)",
        voku: "~100% transparent periphery",
      },
    ],
    [],
  );

  return (
    <div className="min-h-screen bg-black text-white">
      <OpsHeader />
      <TacticalBackdrop />

      {/* HERO */}
      <section className="pt-[118px] md:pt-[132px]">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="grid items-start gap-12 md:grid-cols-2 md:gap-16">
            <div>
              <div className="inline-flex items-center gap-2 rounded-none border border-white/10 bg-white/[0.02] px-3 py-2 text-xs text-white/70">
                <Crosshair className="h-4 w-4 text-[#00E6A8]" />
                <span className="font-mono uppercase tracking-[0.18em]">VOKU Ops</span>
                <span className="text-white/45">|</span>
                <span className="text-white/65">Tactical Analog HUD</span>
              </div>

              <h1 className="mt-5 text-balance text-4xl font-semibold tracking-tight md:text-6xl">
                The Zero-Latency Edge in CQB.
              </h1>
              <p className="mt-4 max-w-xl text-pretty text-base leading-relaxed text-white/75 md:text-lg">
                An ultra-lightweight tactical display built on direct analog transmission. Solving the critical
                failure of digital AR in combat: motion sickness and vestibular dissonance.
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                <Card className="rounded-none border-white/10 bg-white/[0.03] p-4 text-white backdrop-blur-xl">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/55">
                      Latency
                    </div>
                    <Zap className="h-4 w-4 text-[#00E6A8]" />
                  </div>
                  <div className="mt-2 font-mono text-lg text-white">&lt;1ms</div>
                </Card>
                <Card className="rounded-none border-white/10 bg-white/[0.03] p-4 text-white backdrop-blur-xl">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/55">
                      Zero OS
                    </div>
                    <HardDrive className="h-4 w-4 text-white/75" />
                  </div>
                  <div className="mt-2 font-mono text-lg text-white">Un-hackable</div>
                </Card>
                <Card className="rounded-none border-white/10 bg-white/[0.03] p-4 text-white backdrop-blur-xl">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/55">
                      RF
                    </div>
                    <Radio className="h-4 w-4 text-white/75" />
                  </div>
                  <div className="mt-2 font-mono text-lg text-white">No signature</div>
                </Card>
              </div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <RequestPocDialog>
                  <Button className="h-12 rounded-none bg-[#00E6A8] px-6 text-black hover:bg-white hover:text-black">
                    Request PoC Briefing
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Button>
                </RequestPocDialog>
                <Button
                  variant="outline"
                  className="h-12 rounded-none border-white/15 bg-transparent px-6 text-white hover:bg-white/10 hover:text-white"
                  onClick={() => {
                    const el = document.getElementById("problem");
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  Review the failure mode
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="md:pt-2">
              <div className="relative overflow-hidden rounded-none border border-white/10 bg-white/[0.02] backdrop-blur-xl">
                <div className="absolute inset-0 opacity-[0.10] [background-image:linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:64px_64px]" />

                <div className="relative aspect-[16/10] w-full overflow-hidden">
                  <img
                    src={targetDesign}
                    alt="VOKU Ops target design"
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-black/25" />
                </div>

                <div className="relative flex items-center justify-between border-t border-white/10 px-5 py-4">
                  <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/60">
                    target design
                  </div>
                  <div className="text-xs text-white/55">tactical optics • analog-direct</div>
                </div>
              </div>

              <Card className="mt-6 relative overflow-hidden rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
                <div className="absolute inset-0 opacity-[0.10] [background-image:linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:44px_44px]" />
                <div className="relative">
                  <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/55">
                    Operational summary
                  </div>
                  <div className="mt-4 space-y-3">
                    {[
                      {
                        k: "Core mode",
                        v: "Analog-direct HUD",
                      },
                      {
                        k: "Emissions",
                        v: "Zero RF signature",
                      },
                      {
                        k: "Compute",
                        v: "No OS / no apps",
                      },
                      {
                        k: "Reliability",
                        v: "Hardwired signal path",
                      },
                    ].map((it) => (
                      <div
                        key={it.k}
                        className="flex items-center justify-between gap-6 rounded-none border border-white/10 bg-black/30 px-4 py-3"
                      >
                        <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/55">
                          {it.k}
                        </div>
                        <div className="font-mono text-sm text-white/80">{it.v}</div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 rounded-none border border-white/10 bg-black/30 p-4">
                    <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/55">
                      Note
                    </div>
                    <div className="mt-2 text-sm text-white/70">
                      VOKU Ops is a specialized vertical of the VOKU Tech platform.
                      Briefings are shared on a need-to-know basis.
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section id="problem" className="pt-20 md:pt-28">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/55">The problem</div>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight md:text-3xl">
                Why Digital AR Fails on the Battlefield.
              </h2>
            </div>
            <div className="max-w-md text-sm text-white/60">
              Processing video creates a physics barrier: 15–50ms of delay.
            </div>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <Card className="rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
              <div className="text-sm font-semibold text-white">Context</div>
              <p className="mt-3 text-sm leading-relaxed text-white/70 md:text-base">
                Digital AR systems (including multi-billion dollar programs) accumulate latency through capture,
                encoding, compute, and display. In dynamic movement, that delay becomes vestibular mismatch.
              </p>
              <div className="mt-6 rounded-none border border-white/10 bg-black/30 p-4">
                <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/55">Delay</div>
                <div className="mt-2 font-mono text-2xl text-white">15–50ms+</div>
              </div>
            </Card>

            <Card className="rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
              <div className="text-sm font-semibold text-white">Failure modes</div>
              <div className="mt-4 space-y-3">
                {[
                  {
                    t: "Motion Sickness",
                    d: "Vestibular mismatch during dynamic movement.",
                  },
                  {
                    t: "System Failures",
                    d: "Complex OS stacks overheat, crash, and degrade under load.",
                  },
                  {
                    t: "Electronic Warfare (EW)",
                    d: "Wi‑Fi/Bluetooth emissions act as beacons for the enemy.",
                  },
                ].map((b) => (
                  <div
                    key={b.t}
                    className="rounded-none border border-white/10 bg-black/30 p-4"
                  >
                    <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/55">
                      {b.t}
                    </div>
                    <div className="mt-2 text-sm text-white/75">{b.d}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section id="solution" className="pt-20 md:pt-28">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/55">
                The solution
              </div>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight md:text-3xl">
                Physics, not Processors.
              </h2>
            </div>
            <div className="max-w-md text-sm text-white/60">
              Analog video projects directly onto an aspheric lens via a direct optical path.
            </div>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <Card className="rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
              <div className="text-sm font-semibold text-white">Approach</div>
              <p className="mt-3 text-sm leading-relaxed text-white/70 md:text-base">
                VOKU abandons digital OS and image processing completely. The system is hardware-only:
                deterministic, hardwired, and designed to preserve vestibular sync.
              </p>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <div className="rounded-none border border-white/10 bg-black/30 p-4">
                  <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/55">Compute</div>
                  <div className="mt-2 font-mono text-sm text-white/80">None</div>
                </div>
                <div className="rounded-none border border-white/10 bg-black/30 p-4">
                  <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/55">RF</div>
                  <div className="mt-2 font-mono text-sm text-white/80">Zero emissions</div>
                </div>
              </div>
            </Card>

            <Card className="rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
              <div className="text-sm font-semibold text-white">Comparison</div>
              <div className="mt-4">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="font-mono text-white/55">Feature</TableHead>
                      <TableHead className="font-mono text-white/55">Digital AR (IVAS)</TableHead>
                      <TableHead className="font-mono text-white/55">VOKU Ops Analog HUD</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow
                        key={r.feature}
                        className="border-white/10 hover:bg-white/[0.03]"
                      >
                        <TableCell className="font-mono text-white/75">{r.feature}</TableCell>
                        <TableCell className="text-white/70">{r.digital}</TableCell>
                        <TableCell className="text-white/80">
                          <span className="text-[#00E6A8]">{r.voku}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section id="use-cases" className="pt-20 md:pt-28">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/55">
                Use cases
              </div>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight md:text-3xl">
                Engineered for the Assaulter.
              </h2>
            </div>
            <div className="max-w-md text-sm text-white/60">
              Hardware-first overlays for CQB, FPV, thermal, and corner-sight.
            </div>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {[
              {
                title: "Assault FPV (Room Clearing)",
                body: "Feed drone video directly to the eye without losing real-world peripheral vision. Hold a weapon and pilot simultaneously.",
              },
              {
                title: "Zero-Lag Corner-Sight",
                body: "Hardwire to a rifle cam. Shoot from cover in real-time. A 50ms digital lag means missing a moving target; VOKU ensures pixel-to-reality sync.",
              },
              {
                title: "Thermal / NVG Overlay",
                body: "Instant, unjammable data feed from commander optics or thermals without taking eyes off the sector.",
              },
            ].map((c) => (
              <Card
                key={c.title}
                className="rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl"
              >
                <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/55">
                  Tactical
                </div>
                <div className="mt-2 text-lg font-semibold text-white">{c.title}</div>
                <p className="mt-3 text-sm leading-relaxed text-white/70">{c.body}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* STATUS */}
      <section id="status" className="pt-20 md:pt-28">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="grid gap-6 md:grid-cols-2">
            <Card className="rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/55">
                Status
              </div>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight md:text-3xl">
                Ready for the Field.
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-white/70 md:text-base">
                Functional engineering prototypes are complete. VOKU Ops is currently advancing to Field Testing
                and Proof of Concept (PoC) validation.
              </p>
            </Card>

            <Card className="relative overflow-hidden rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
              <div className="absolute inset-0 opacity-[0.10] [background-image:linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:44px_44px]" />
              <div className="relative">
                <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/55">
                  PoC
                </div>
                <div className="mt-3 text-sm text-white/70">
                  Request a classified briefing packet and test plan outline.
                </div>

                <div className="mt-6">
                  <RequestPocDialog>
                    <Button className="h-12 w-full rounded-none bg-[#00E6A8] text-black hover:bg-white hover:text-black">
                      Request PoC Briefing
                      <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Button>
                  </RequestPocDialog>
                </div>

                <div className="mt-5 text-xs font-mono text-white/45">
                  ACCESS: by approval • channel: offline-first
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="mt-20 border-t border-white/10 pb-10 pt-10 md:mt-28">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-white/70">VOKU Technologies Inc. | Est. 2026, Florida, USA.</div>
            <div className="flex items-center gap-4 text-sm text-white/60">
              <Link to="/" className="hover:text-white">Home</Link>
              <Link to="/vision" className="hover:text-white">Vision</Link>
              <a href="mailto:contact@vokutech.com" className="hover:text-white">
                Contact
              </a>
            </div>
          </div>
          <div className="mt-8 flex items-center justify-between border-t border-white/10 pt-6 text-xs text-white/45">
            <span className="font-mono">© {new Date().getFullYear()} VOKU</span>
            <span className="font-mono">OPS / BRIEFING</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
